import { useLayoutEffect, useMemo, useRef } from 'react';
import { useStore } from 'react-redux';
import { useNavigate } from 'react-router';
import { defaultTo } from '../utils/fp';
import { getNextLocationState, getNextSearch } from '../lib/navigation';
import { getLocationState, getPathname } from '../selectors/router';

// Navigation for the vis and search pages: merge the payload into the current
// location state, serialise it to the search string and hand the whole thing to
// react-router. A nil pathname keeps the current one.
//
// The store is read when push/replace is called, not when the hook runs, so the
// location a navigation starts from is the one the store holds at that moment.
export const useVisNavigate = () => {
  const navigate = useNavigate();
  const store = useStore();

  // useNavigate returns a new function whenever the location changes. This hook
  // is consumed on the render-critical path, so the object it returns is kept
  // stable: only relative navigation depends on what changes there, and every
  // pathname here is absolute.
  const navigateRef = useRef(navigate);
  useLayoutEffect(() => {
    navigateRef.current = navigate;
  });

  return useMemo(() => {
    const go = replace => (pathname, payload) => {
      const state = store.getState();
      const locationState = getNextLocationState(getLocationState(state), payload);
      const search = getNextSearch(locationState);
      // the basename is prepended by the router itself
      const to = { pathname: defaultTo(getPathname(state), pathname), search };
      navigateRef.current(to, { replace, state: locationState });
    };

    return {
      push: go(false),
      replace: go(true),
    };
  }, [store]);
};
